import { Helmet } from "react-helmet-async";
import { PageHero } from "@/components/shared/PageHero";
import { ServiceSidebar } from "@/components/services/ServiceSidebar";
import { CTASection } from "@/components/shared/CTASection";
import { ServiceFAQ } from "@/components/services/ServiceFAQ";
import { aerationFaqs } from "@/data/serviceFaqs";
import { Leaf, Sprout, Zap, Shield, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import dethatchImage from "@/assets/services/dethatching.jpg";

const features = [
  {
    icon: Leaf,
    title: "Thatch Removal",
    description: "Power dethatching removes the layer of dead grass, roots, and debris that builds up between soil and grass blades.",
  },
  {
    icon: Sprout,
    title: "Improved Grass Health",
    description: "Removing thatch allows water, air, and nutrients to reach grass roots more effectively.",
  },
  {
    icon: Zap,
    title: "Faster Recovery",
    description: "Your lawn will green up faster in spring and stay healthier throughout the growing season.",
  },
  {
    icon: Shield,
    title: "Disease Prevention",
    description: "Thick thatch layers harbor pests and diseases. Dethatching creates a healthier lawn environment.",
  },
];

const dethatchingFaqs = [
  { question: "What is dethatching?", answer: "Dethatching removes the layer of dead grass, roots, and debris that accumulates between the soil and living grass blades. A thin thatch layer is normal, but thick buildup prevents water and nutrients from reaching roots." },
  { question: "How do I know if my lawn needs dethatching?", answer: "If your lawn feels spongy when walking on it, or if you can see a thick brown layer between the grass blades and soil (more than 1/2 inch), it's time to dethatch." },
  { question: "When is the best time to dethatch in Minnesota?", answer: "Early spring (late April to early May) or early fall (September) are ideal times to dethatch cool-season grasses in Minnesota." },
  { question: "How often should I dethatch my lawn?", answer: "Most lawns benefit from dethatching every 1-3 years, depending on grass type and how quickly thatch accumulates." },
  { question: "Is dethatching the same as aeration?", answer: "No, they're different services. Dethatching removes organic buildup, while aeration removes soil plugs to reduce compaction. Both are beneficial and work well together." },
  { question: "Will dethatching damage my lawn?", answer: "Your lawn may look rough immediately after dethatching, but it will recover quickly and come back stronger. We recommend overseeding after dethatching for best results." },
  { question: "Should I combine dethatching with other services?", answer: "Yes! Dethatching pairs well with aeration and overseeding. This combination gives your lawn the best chance to thrive." },
  { question: "Do you remove the thatch debris?", answer: "Yes, our service includes complete cleanup. We rake and remove all thatch debris from your property." },
];

const Dethatching = () => {
  return (
    <>
      <Helmet>
        <title>Dethatching Services | Riekels Lawn Care & Landscaping</title>
        <meta
          name="description"
          content="Professional lawn dethatching in the West Twin Cities Metro, MN. Remove thatch buildup and improve lawn health. Call (763) 245-7813."
        />
      </Helmet>
      <main>
        <PageHero
          badge="Our Services"
          title="Dethatching"
          subtitle="Remove thatch buildup and give your lawn room to breathe and thrive."
        />

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
              <div className="lg:col-span-1">
                <ServiceSidebar />
              </div>
              <div className="lg:col-span-3">
                <div className="mb-8 rounded-xl overflow-hidden shadow-lg">
                  <img 
                    src={dethatchImage} 
                    alt="Professional lawn dethatching" 
                    className="w-full h-64 md:h-80 object-cover object-center"
                  />
                </div>

                <div className="prose prose-lg max-w-none mb-12">
                  <h2 className="text-3xl font-heading font-bold text-foreground mb-6">
                    Give Your Lawn Room to Grow
                  </h2>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Thatch is a layer of dead grass, roots, and organic debris that accumulates between 
                    the soil surface and your living grass blades. While a thin layer of thatch is normal 
                    and even beneficial, thick thatch buildup (over 1/2 inch) can suffocate your lawn.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    When thatch gets too thick, it prevents water, air, and fertilizer from reaching the 
                    roots. It also creates an ideal environment for pests and lawn diseases. Professional 
                    dethatching removes this barrier and revitalizes your lawn.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {features.map((feature, index) => (
                    <article key={index} className="feature-card">
                      <div className="icon-circle mb-4">
                        <feature.icon className="w-6 h-6" />
                      </div>
                      <h3 className="text-lg font-heading font-bold text-foreground mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-muted-foreground">
                        {feature.description}
                      </p>
                    </article>
                  ))}
                </div>

                <div className="mt-12 bg-secondary rounded-2xl p-8 text-center">
                  <Leaf className="w-12 h-12 mx-auto mb-4 text-secondary-foreground" />
                  <h3 className="text-2xl font-heading font-bold text-secondary-foreground mb-3">
                    Schedule Dethatching Service
                  </h3>
                  <p className="text-secondary-foreground/80 mb-6 max-w-lg mx-auto">
                    Spring and fall are the best times to dethatch. Book your service today.
                  </p>
                  <Link to="/contact">
                    <Button size="lg" className="bg-white hover:bg-white/90 text-secondary font-semibold">
                      Request a Quote <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <ServiceFAQ faqs={dethatchingFaqs} />

        <CTASection />
      </main>
    </>
  );
};

export default Dethatching;
